<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { ExternalLink, Search, ShieldCheck, ShieldX, X } from '@lucide/vue';
import { useRouter } from 'vue-router';
import AppShell from '../components/AppShell.vue';
import { api } from '../api/client';
import RichText from '../components/RichText.vue';

const router = useRouter();
const events = ref<any[]>([]);
const loading = ref(true);
const error = ref('');
const search = ref('');
const status = ref<'all' | 'accepted' | 'declined' | 'revoked'>('all');
const revokingId = ref('');
const revocationReason = ref('');
const actionError = ref('');
const savingRevocation = ref(false);
const eraseOnRevoke = ref(true);
const total = ref(0);
const nextCursor = ref<string | null>(null);
const loadingMore = ref(false);
let requestId = 0;
let searchTimer: ReturnType<typeof setTimeout> | undefined;

// Фильтрация и поиск выполняются на сервере, страницы подгружаются по курсору.
async function loadEvents(append = false) {
  const current = ++requestId;
  if (append) loadingMore.value = true; else loading.value = true;
  error.value = '';
  try {
    const page = await api.consentEvents({ status: status.value, q: search.value.trim(), cursor: append ? nextCursor.value : null });
    if (current !== requestId) return;
    events.value = append ? [...events.value, ...page.events] : page.events;
    total.value = page.total;
    nextCursor.value = page.nextCursor;
  } catch (e: any) {
    if (e.status === 401) return router.push('/login');
    error.value = e.message;
  } finally {
    if (current === requestId) { loading.value = false; loadingMore.value = false; }
  }
}

watch(status, () => loadEvents());
watch(search, () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => loadEvents(), 300); });

function startRevocation(eventId: string) {
  revokingId.value = eventId;
  // По умолчанию анкета удаляется при отзыве обязательного согласия.
  eraseOnRevoke.value = Boolean(events.value.find((item) => item.id === eventId)?.required);
  revocationReason.value = '';
  actionError.value = '';
}

function cancelRevocation() {
  revokingId.value = '';
  revocationReason.value = '';
  actionError.value = '';
}

async function revokeConsent(event: any) {
  savingRevocation.value = true;
  actionError.value = '';
  try {
    if (eraseOnRevoke.value && !confirm('Удалить ответы и файлы этой анкеты? Факт заполнения и журнал согласий сохранятся. Действие нельзя отменить.')) return;
    const result = await api.revokeConsent(event.id, revocationReason.value, eraseOnRevoke.value);
    event.revoked = true;
    event.revokedAt = result.revokedAt;
    event.revocationReason = revocationReason.value;
    event.dataErased = result.dataErased;
    if (result.dataErased) events.value.filter((item) => item.responseId === event.responseId).forEach((item) => { item.responseErasedAt = result.revokedAt; });
    cancelRevocation();
  } catch (e: any) {
    actionError.value = e.message;
  } finally {
    savingRevocation.value = false;
  }
}

onMounted(async () => {
  try {
    await api.me();
  } catch (e: any) {
    if (e.status === 401) return router.push('/login');
  }
  await loadEvents();
});
</script>

<template>
  <AppShell>
    <div class="page-heading journal-heading">
      <div><h1>Журнал согласий</h1><p>События по всем формам · {{ total }} записей</p></div>
    </div>
    <div class="journal-filters">
      <label class="search-control"><Search :size="17"/><input v-model="search" placeholder="Форма, согласие или ID ответа" aria-label="Поиск по журналу"/></label>
      <select v-model="status" class="control journal-status" aria-label="Статус согласия"><option value="all">Все статусы</option><option value="accepted">Принято</option><option value="revoked">Отозвано</option><option value="declined">Не принято</option></select>
    </div>
    <div v-if="loading" class="journal-table skeleton"></div>
    <p v-else-if="error" class="alert error">{{ error }}</p>
    <section v-else-if="!events.length" class="empty-state journal-empty"><span class="empty-icon"><ShieldCheck :size="30"/></span><h2>Записей пока нет</h2><p>{{ search || status !== 'all' ? 'Измените параметры поиска.' : 'Записи появятся после отправки формы с настроенными согласиями.' }}</p></section>
    <div v-else class="journal-table">
      <div class="journal-row journal-header"><span>Дата</span><span>Форма</span><span>Согласие</span><span>Статус</span><span>Ответ</span></div>
      <article v-for="event in events" :key="event.id" class="journal-row">
        <time>{{ new Date(event.recordedAt).toLocaleString('ru-RU') }}</time>
        <span v-if="event.formDeleted" class="journal-form-deleted">{{ event.formTitle }}<small>форма удалена</small></span><button v-else class="journal-form-link" @click="router.push(`/forms/${event.formId}/edit`)">{{ event.formTitle }}<ExternalLink :size="14"/></button>
        <div class="journal-consent"><RichText tag="strong" :text="event.text"/><small v-if="event.responseErasedAt" class="erased-note">{{ event.responseErasureReason || 'Анкета удалена' }}</small><small v-if="event.presentation === 'dialog'">Принято через окно с полным текстом</small><details v-if="event.fullText" class="consent-full"><summary>Полный текст согласия</summary><RichText tag="div" :text="event.fullText"/></details><small>Версия {{ event.contentHash.slice(0, 10) }}<template v-if="event.policyUrl"> · <a :href="event.policyUrl" target="_blank" rel="noopener noreferrer">{{ event.policyLabel || 'Политика' }}</a></template></small><small v-if="event.revoked">Отозвано {{ new Date(event.revokedAt).toLocaleString('ru-RU') }}<template v-if="event.revocationReason"> · {{ event.revocationReason }}</template></small><div v-if="revokingId === event.id" class="revocation-editor"><textarea v-model="revocationReason" class="control textarea compact" maxlength="500" placeholder="Причина отзыва — необязательно"/><label class="check-item erase-option"><input v-model="eraseOnRevoke" type="checkbox"/>Удалить данные анкеты (ответы и файлы)</label><p v-if="actionError" class="field-error">{{ actionError }}</p><div><button class="btn btn-danger" :disabled="savingRevocation" @click="revokeConsent(event)"><ShieldX :size="15"/>Подтвердить отзыв</button><button class="icon-btn" title="Отмена" @click="cancelRevocation"><X :size="17"/></button></div></div></div>
        <div class="journal-event-status"><span :class="['consent-state', event.revoked ? 'revoked' : event.accepted ? 'accepted' : 'declined']">{{ event.revoked ? 'Отозвано' : event.accepted ? 'Принято' : 'Не принято' }}</span><button v-if="event.accepted && !event.revoked && revokingId !== event.id" class="revoke-link" @click="startRevocation(event.id)">Отозвать</button></div>
        <span v-if="event.formDeleted" class="journal-response-link muted">{{ event.responseId.slice(0, 8) }}</span><button v-else class="journal-response-link" @click="router.push(`/forms/${event.formId}/responses#response-${event.responseId}`)">{{ event.responseId.slice(0, 8) }}</button>
      </article>
    </div>
    <div v-if="!loading && !error && nextCursor" class="load-more"><button class="btn btn-quiet" :disabled="loadingMore" @click="loadEvents(true)">{{ loadingMore ? 'Загрузка…' : `Показать ещё (загружено ${events.length} из ${total})` }}</button></div>
  </AppShell>
</template>
